import React from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui';
import { fetchRulesetChanges } from '../lib/ruleset-service';
import type {
  ImportRuleset,
  RulesetChangesPage,
  RulesetCodeChange,
} from '../lib/ruleset-service';

const PAGE_SIZE = 25;

const money = (value: number | null) =>
  value === null
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

const onOff = (value: boolean) => (value ? 'On' : 'Off');

const ChangeLine = ({ change }: { change: RulesetCodeChange }) => (
  <div className='ih-rules-change'>
    <span className='ih-rules-change-code'>{change.label || change.code}</span>{' '}
    {onOff(change.previous_value)} → {onOff(change.applied_value)}
    {change.current_value !== null &&
      change.current_value !== change.applied_value && (
        <span className='ih-rules-change-note'>
          {' '}
          · since set {onOff(change.current_value)} by a user
        </span>
      )}
  </div>
);

/**
 * What the active ruleset changed on a database's products: each changed code
 * and the estimated duty before the ruleset and now.
 */
const RulesetChangesDialog = ({
  databaseId,
  ruleset,
  onClose,
}: {
  databaseId: string;
  ruleset: ImportRuleset | null;
  onClose: () => void;
}): JSX.Element => {
  const [page, setPage] = React.useState(1);
  const [changes, setChanges] = React.useState<RulesetChangesPage | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const rulesetId = ruleset?.id;

  React.useEffect(() => {
    setPage(1);
    setChanges(null);
  }, [rulesetId]);

  React.useEffect(() => {
    if (!rulesetId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchRulesetChanges(databaseId, rulesetId, page, PAGE_SIZE).then(
      (loaded) => {
        if (cancelled) return;
        setLoading(false);
        if (loaded.success && loaded.changes) setChanges(loaded.changes);
        else setError(loaded.error ?? 'Failed to load the ruleset changes');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [databaseId, rulesetId, page]);

  const totals = changes?.totals;
  const pages = totals ? Math.ceil(totals.products / PAGE_SIZE) : 0;
  const difference = totals ? totals.duty_now - totals.duty_before : 0;

  return (
    <Dialog
      open={!!ruleset}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {ruleset && (
        <DialogContent size='xl' onClose={onClose}>
          <DialogHeader>
            <DialogTitle>Changes from “{ruleset.name}”</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {error ? (
              <p className='ih-text-sm ih-text-muted'>{error}</p>
            ) : !changes ? (
              <p className='ih-text-sm ih-text-muted'>
                <span className='ih-spinner ih-db-settings-spinner' /> Loading
                changes…
              </p>
            ) : totals && totals.products === 0 ? (
              <p className='ih-text-sm ih-text-muted'>
                This ruleset hasn’t changed any products yet.
              </p>
            ) : (
              <>
                <div className='ih-rules-changes-stats'>
                  <div>
                    <span>Products changed</span>
                    <strong className='ih-num'>
                      {totals?.products.toLocaleString()}
                    </strong>
                  </div>
                  <div>
                    <span>Estimated duty</span>
                    <strong className='ih-num'>
                      {money(totals?.duty_before ?? 0)} →{' '}
                      {money(totals?.duty_now ?? 0)}
                    </strong>
                  </div>
                  <div>
                    <span>Difference</span>
                    <strong
                      className={`ih-num ${
                        difference < 0
                          ? 'is-lower'
                          : difference > 0
                          ? 'is-higher'
                          : ''
                      }`}
                    >
                      {difference > 0 ? '+' : ''}
                      {money(difference)}
                    </strong>
                  </div>
                </div>
                <p className='ih-text-xs ih-text-muted ih-mt-2'>
                  “Before” is the estimated duty (MPF and HMF excluded) just
                  before the ruleset first changed each product; “now” is its
                  latest result, so later edits and rate changes count too.
                  {totals && totals.compared_products < totals.products
                    ? ` Totals cover the ${totals.compared_products.toLocaleString()} products with both amounts.`
                    : ''}
                </p>
                <div className='ih-rules-changes-table'>
                  <table className='ih-table'>
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>What changed</th>
                        <th className='ih-num'>Duty before</th>
                        <th className='ih-num'>Duty now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.products.map((product) => (
                        <tr key={product.database_product_id}>
                          <td>
                            <Link
                              className='ih-rules-changes-product'
                              to={`/import-hub/products/${product.product_id}`}
                            >
                              {product.name}
                            </Link>
                            <div className='ih-text-xs ih-text-muted ih-num'>
                              {[product.hts_code, product.country_of_origin]
                                .filter(Boolean)
                                .join(' · ')}
                            </div>
                          </td>
                          <td>
                            {product.changes.map((change) => (
                              <ChangeLine
                                key={`${change.kind}:${change.code}`}
                                change={change}
                              />
                            ))}
                          </td>
                          <td className='ih-num'>
                            {money(product.duty_before)}
                          </td>
                          <td className='ih-num'>{money(product.duty_now)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pages > 1 && (
                  <div
                    className='ih-priority-pagination'
                    aria-label='Change pages'
                  >
                    <Button
                      variant='outline'
                      size='sm'
                      disabled={loading || page <= 1}
                      onClick={() => setPage((current) => current - 1)}
                    >
                      Previous
                    </Button>
                    <span className='ih-text-xs ih-text-muted ih-num'>
                      Page {page} of {pages}
                    </span>
                    <Button
                      variant='outline'
                      size='sm'
                      disabled={loading || page >= pages}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </DialogBody>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default RulesetChangesDialog;
